import { useState, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { FiMessageSquare, FiSend, FiX, FiSmile, FiFrown, FiMeh, FiAlertCircle, FiZap, FiRefreshCw } from 'react-icons/fi'
import { useTranslation } from '../../hooks/useTranslation'
import { apiClient } from '../../api/client'
import toast from 'react-hot-toast'

// Force reload after icon fix
export default function FeedbackModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [type, setType] = useState<'bug' | 'feature' | 'improvement'>('improvement')
  const [satisfaction, setSatisfaction] = useState<number>(3)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { translate, language } = useTranslation()
  const isRTL = language === 'ar'

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error(isRTL ? 'يرجى كتابة رسالتك' : 'Please enter your message')
      return
    }

    setIsSubmitting(true)
    try {
      // In a real app, this would send to an API
      // await apiClient.post('/api/feedback', { type, satisfaction, message, url: window.location.href })
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      toast.success(isRTL ? 'شكراً لك! تم إرسال ملاحظاتك بنجاح' : 'Thank you! Your feedback has been sent successfully')
      setIsOpen(false)
      setMessage('')
      setSatisfaction(3)
    } catch (error) {
      toast.error(isRTL ? 'فشل في إرسال الملاحظات' : 'Failed to send feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 z-40 bg-primary-600 text-white p-3 rounded-full shadow-lg hover:bg-primary-700 transition-all duration-300 hover:scale-110 group"
        title={isRTL ? 'إرسال ملاحظات' : 'Send Feedback'}
      >
        <FiMessageSquare size={20} />
        <span className={`absolute ${isRTL ? 'right-full mr-2' : 'left-full ml-2'} top-1/2 -translate-y-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap`}>
          {isRTL ? 'شاركنا برأيك' : 'Share your feedback'}
        </span>
      </button>

      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-white flex items-center gap-2">
                      <FiMessageSquare />
                      {isRTL ? 'أرسل ملاحظاتك' : 'Send Feedback'}
                    </Dialog.Title>
                    <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
                      <FiX size={20} />
                    </button>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Satisfaction */}
                    <div className="text-center">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {isRTL ? 'ما مدى رضاك عن النظام؟' : 'How satisfied are you with the system?'}
                      </label>
                      <div className="flex justify-center gap-4">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <button
                            key={level}
                            onClick={() => setSatisfaction(level)}
                            className={`p-3 rounded-xl transition-all ${
                              satisfaction === level 
                                ? 'bg-primary-100 text-primary-600 scale-110 ring-2 ring-primary-500' 
                                : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                            }`}
                          >
                            {level === 1 && <FiFrown size={24} />}
                            {level === 2 && <FiMeh size={24} className="opacity-70" />}
                            {level === 3 && <FiMeh size={24} />}
                            {level === 4 && <FiSmile size={24} className="opacity-70" />}
                            {level === 5 && <FiSmile size={24} />}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Feedback Type */}
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setType('bug')}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          type === 'bug' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        <FiAlertCircle size={20} />
                        <span className="text-xs font-bold">{isRTL ? 'خطأ برمي' : 'Bug'}</span>
                      </button>
                      <button
                        onClick={() => setType('improvement')}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          type === 'improvement' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        <FiMessageSquare size={20} />
                        <span className="text-xs font-bold">{isRTL ? 'تحسين' : 'Improve'}</span>
                      </button>
                      <button
                        onClick={() => setType('feature')}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          type === 'feature' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : 'border-gray-100 text-gray-500 hover:border-gray-200'
                        }`}
                      >
                        <FiZap size={20} />
                        <span className="text-xs font-bold">{isRTL ? 'ميزة جديدة' : 'Feature'}</span>
                      </button>
                    </div>

                    {/* Message */}
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {isRTL ? 'رسالتك' : 'Your Message'}
                      </label>
                      <textarea
                        rows={4}
                        className={`input-field w-full resize-none ${isRTL ? 'text-right' : 'text-left'}`}
                        placeholder={isRTL ? 'أخبرنا المزيد عن تجربتك أو مقترحاتك...' : 'Tell us more about your experience or suggestions...'}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="w-full btn-primary py-3 flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? <FiRefreshCw className="animate-spin" /> : <FiSend />}
                      {isRTL ? 'إرسال' : 'Send Feedback'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
